import { FC, FocusEvent, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Icon, Tooltip } from "capitalrx-components-2.0/lib/general";
import { Menu } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";

import { ClientAwareLink, ScreenReader, ThemedTopNavButton } from "@/App/components";
import { LanguageSwitcher } from "@/App/components/LanguageSwitcher";
import { AuthenticationContext, ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { MemberEligibilityInfo } from "@/App/types";

import { ThemedLink } from "../themed";
import { FullWidthDropdownMenu } from "./FullWidthDropdownMenu";
import { FullWidthDropdownMenuButton } from "./FullWidthDropdownMenu";
import { MenuItem } from "./MenuBar";

const DesktopMenuBarContainer = styled(Shelf)`
  background-color: white;
  height: 4.375rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 8px;
  border-bottom: solid 1px ${Color.toString(Color.LightGrey[200])};
  z-index: 1000;
`;

const ClientAwareMenuLink = styled(ClientAwareLink)`
  font-size: 16px;

  color: #313e48 !important;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TopLevelClientAwareMenuLink = styled(ClientAwareMenuLink)`
  font-family: "Source Sans 3", sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;
  white-space: nowrap;

  &:hover {
    color: #0071b3 !important;
  }
`;

const StyledHeading = styled(Heading)`
  font-family: Source Sans 3;
  font-weight: 600;
  font-style: SemiBold;
  font-size: 18px;
`;

export const MenuLink = styled(ThemedLink)`
  color: #313e48 !important;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  font-family: "Source Sans 3", sans-serif;
  font-weight: 600;
  font-style: normal;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0px;

  &:hover {
    color: #0071b3 !important;
  }

  path {
    fill: #0071b3 !important;
  }
`;

const SubMenuLink = styled(ThemedLink)`
  font-size: 16px;
  color: #313e48 !important;
  text-decoration: none;
  white-space: nowrap;
  font-family: "Source Sans 3", sans-serif;

  path {
    fill: #0071b3 !important;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const MenuItemShelf = styled(Shelf)`
  min-width: 180px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 12px;
`;

const Root = styled.nav`
  font-size: 14px;
  line-height: 24px;
  color: ${Color.toString(Color.DarkGrey[600])};
  height: 7.125rem;

  .menu-button {
    button {
      height: 100%;
    }

    &:focus {
      outline: none;
    }
  }

  .bar {
    height: 2.75rem;
    width: 100%;
    padding: 0 1rem;
    background-color: ${(props) => props.theme?.primary_color};
    z-index: 1000;
  }

  .module-name {
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 600;
    line-height: 0.875rem;
    text-transform: capitalize;
    color: ${Color.toString(Color.White)};
    cursor: default;
  }

  & > .bar-container {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 90000000;
    box-sizing: border-box;

    &:focus {
      outline: none;
    }

    & > .menu {
      position: absolute;
      transform: translateY(-100%);
      background-color: ${Color.toString(Color.White)};
      transition: transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s;
      width: 100%;
      left: 0;
      top: 113px;
      z-index: -1;
      padding: 2.5rem;
      box-shadow: 0px 0px 8px transparent;
      transition:
        transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s,
        box-shadow 0.5s linear 0.3s;
      overflow-y: auto;
      max-height: calc(100vh - 3.5rem);

      &:focus {
        outline: none;
      }

      &.expanded {
        transform: translateY(0);
        box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.16);
        transition:
          transform cubic-bezier(0.64, 0.11, 1, 1) 0.3s,
          box-shadow 0s linear 0s;
      }
    }
  }
`;

interface DesktopMenuBarProps {
  homeItems: MenuItem[];
  claimsAndCoverageMenuItems: MenuItem[];
  careSearchMenuItems: MenuItem[];
  dynamicLinkItems: MenuItem[];
  onCommonMenuItemClick: (menuItem: MenuItem) => void;
  externalConnectionItems: MenuItem[];
}

export const DesktopMenuBar: FC<DesktopMenuBarProps> = ({
  claimsAndCoverageMenuItems,
  careSearchMenuItems,
  dynamicLinkItems,
  externalConnectionItems,
  onCommonMenuItemClick,
  homeItems,
}) => {
  const [claimsAndCoverageMenuExpanded, setClaimsAndCoverageMenuExpanded] = useState(false);
  const [careSearchMenuExpanded, setCareSearchMenuExpanded] = useState(false);
  const claimsAndCoverageButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const careSearchButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const claimsAndCoverageMenuRef = useRef<HTMLDivElement | null>(null);
  const careSearchMenuRef = useRef<HTMLDivElement | null>(null);

  const { memberEligibility, availableEligibility, loggedInEligibility, setActiveMemberEligibility } = useContext(MemberEligibilityContext);
  const { loginRedirectUrl, hideInboxPage, hideFormDocumentsPage, hidePreferences, overrideLandingPage, experience } =
    useContext(ExperienceContext);
  const { logoutUser } = useContext(AuthenticationContext);
  const navigateClient = useNavigateClient();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();

  const accountMenuItems = useMemo(() => {
    const account: MenuItem[] = [];
    if (!hideInboxPage) {
      account.push({
        displayText: translate("MENU_BAR.inbox"),
        to: "/inbox",
        analyticsEvent: ANALYTIC_EVENTS.INBOX.topNav,
      });
    }
    if (!hideFormDocumentsPage) {
      account.push({
        to: "/forms-and-documents",
        displayText: translate("MENU_BAR.formsAndDocuments"),
        analyticsEvent: ANALYTIC_EVENTS.FORMS_AND_DOCUMENTS.topNav,
      });
    }
    if (!hidePreferences) {
      account.push({
        displayText: translate("MENU_BAR.preferences"),
        to: "/notifications",
        analyticsEvent: ANALYTIC_EVENTS.PRFERENCES.topNav,
      });
    }
    return account;
  }, [hideInboxPage, hideFormDocumentsPage, hidePreferences]);

  const onEligibilitySelected = useCallback(
    (elig: MemberEligibilityInfo) => {
      setActiveMemberEligibility(elig);
    },
    [availableEligibility],
  );

  const getEligibilityKey = (elig: MemberEligibilityInfo) => {
    return `${elig.plan.client_id}-${elig.member.external_member_id}-${elig.member.person_code}`;
  };

  const signOut = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.SIGN_OUT_BUTTON);
    await logoutUser();
    if (loginRedirectUrl) {
      navigate(loginRedirectUrl);
    } else {
      navigateClient("/login");
    }
  }, [logoutUser, loginRedirectUrl]);

  const viewerSwitcher = useMemo(() => {
    if (availableEligibility.length > 1) {
      return (
        <ThemedTopNavButton
          dynamicCaret={true}
          appearance="text"
          label={
            <>
              {translate("MENU_BAR.viewingAs")} {memberEligibility.member.first_name} {memberEligibility.member.last_name}
            </>
          }
        >
          {availableEligibility.map((elig) => {
            return (
              <Menu.Item onClick={() => onEligibilitySelected(elig)} key={getEligibilityKey(elig)}>
                <>
                  {elig.member.first_name} {elig.member.last_name}
                </>
              </Menu.Item>
            );
          })}
        </ThemedTopNavButton>
      );
    } else {
      return null;
    }
  }, [memberEligibility, availableEligibility]);

  const onLogoClicked = useCallback(() => {
    navigateClient(overrideLandingPage ?? "/");
  }, [overrideLandingPage]);

  const blurHandler = useCallback(
    (ev: FocusEvent): void => {
      const { relatedTarget } = ev;

      if (relatedTarget) {
        if (claimsAndCoverageMenuExpanded) {
          if (relatedTarget === claimsAndCoverageButtonContainerRef?.current) return;
          if (
            relatedTarget === claimsAndCoverageMenuRef?.current
            || (claimsAndCoverageMenuRef.current && claimsAndCoverageMenuRef.current.contains(relatedTarget as Node))
          )
            return;
          setClaimsAndCoverageMenuExpanded(false);
        } else if (careSearchMenuExpanded) {
          if (relatedTarget === careSearchButtonContainerRef?.current) return;
          if (
            relatedTarget === careSearchMenuRef?.current
            || (careSearchMenuRef.current && careSearchMenuRef.current.contains(relatedTarget as Node))
          )
            return;
          setCareSearchMenuExpanded(false);
        }
      }
    },
    [claimsAndCoverageMenuExpanded, careSearchMenuExpanded],
  );

  const firstName = loggedInEligibility.member.first_name;
  const lastName = loggedInEligibility.member.last_name;

  let accountName = translate("MENU_BAR.account") + ": " + firstName;

  if (firstName && lastName) {
    accountName = accountName + " " + lastName;
  }

  return (
    <Root data-testid="menubar">
      <div className="bar-container" tabIndex={-1} onBlur={blurHandler}>
        <Stack spacing="none">
          <Shelf className="bar" alignment="center">
            <Shelf.Spacer />
            <LanguageSwitcher />
            {viewerSwitcher}
            <ThemedTopNavButton
              dynamicCaret={true}
              appearance="text"
              icon="user"
              label={
                <Tooltip content={accountName}>
                  {(ref) => (
                    <span ref={ref} className="overflow-ellipsis">
                      {accountName}
                    </span>
                  )}
                </Tooltip>
              }
            >
              <Menu.Group>
                {accountMenuItems.map((menuItem, idx) => {
                  return (
                    <Menu.ComponentItem key={menuItem.to + idx.toString()}>
                      <MenuItemShelf>
                        {menuItem.openBehavior === "externalPage" && (
                          <MenuLink onClick={() => onCommonMenuItemClick(menuItem)} to={menuItem.to}>
                            {menuItem.displayText}
                          </MenuLink>
                        )}
                        {!menuItem.openBehavior && (
                          <ClientAwareMenuLink analyticsEvent={menuItem.analyticsEvent} to={menuItem.to}>
                            {menuItem.displayText}
                          </ClientAwareMenuLink>
                        )}
                        <Shelf.Spacer />
                      </MenuItemShelf>
                    </Menu.ComponentItem>
                  );
                })}
              </Menu.Group>
              <Menu.Group>
                <Menu.ComponentItem>
                  <MenuItemShelf>
                    <MenuLink to="/login" onClick={signOut}>
                      {translate("MENU_BAR.signOut")}
                    </MenuLink>
                    <Shelf.Spacer />
                  </MenuItemShelf>
                </Menu.ComponentItem>
              </Menu.Group>
            </ThemedTopNavButton>
          </Shelf>

          <DesktopMenuBarContainer spacing="large">
            <ScreenReader>{translate("MENU_BAR.home")}</ScreenReader>
            <img
              style={{ cursor: "pointer" }}
              data-testid="img-logo"
              onClick={onLogoClicked}
              height={30}
              src={experience?.theme?.logo_desktop}
              alt="Client Logo"
            />
            {homeItems.map((menuItem, idx) => {
              return (
                <TopLevelClientAwareMenuLink
                  key={menuItem.to + idx.toString()}
                  to={menuItem.to}
                  onClick={() => onCommonMenuItemClick(menuItem)}
                >
                  {menuItem.displayText}
                </TopLevelClientAwareMenuLink>
              );
            })}
            {claimsAndCoverageMenuItems.length > 0 && (
              <FullWidthDropdownMenuButton
                label={translate("MENU_BAR.claimsAndCoverage")}
                expanded={claimsAndCoverageMenuExpanded}
                setExpanded={setClaimsAndCoverageMenuExpanded}
                menuRef={claimsAndCoverageMenuRef}
                buttonContainerRef={claimsAndCoverageButtonContainerRef}
              />
            )}
            {careSearchMenuItems.length > 0 && (
              <FullWidthDropdownMenuButton
                label={translate("MENU_BAR.careSearch")}
                expanded={careSearchMenuExpanded}
                setExpanded={setCareSearchMenuExpanded}
                menuRef={careSearchMenuRef}
                buttonContainerRef={careSearchButtonContainerRef}
              />
            )}
            {dynamicLinkItems?.map((menuItem, idx) => {
              if (!menuItem.openBehavior) {
                return (
                  <TopLevelClientAwareMenuLink
                    key={menuItem.to + idx.toString()}
                    to={menuItem.to}
                    testId={`dynamic-link-menu-item-${idx}`}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                  >
                    {menuItem.displayText}
                  </TopLevelClientAwareMenuLink>
                );
              }
              if (menuItem.openBehavior === "externalPage") {
                return (
                  <MenuLink
                    key={menuItem.to + idx.toString()}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                    testId={`dynamic-link-menu-item-${idx}`}
                    to={menuItem.to}
                  >
                    <Shelf>
                      {menuItem.displayText}
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </MenuLink>
                );
              }
            })}
            {externalConnectionItems.map((menuItem, idx) => {
              if (!menuItem.openBehavior) {
                return (
                  <TopLevelClientAwareMenuLink
                    key={menuItem.to + idx.toString()}
                    to={menuItem.to}
                    testId={`external-connection-menu-item-${idx}`}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                  >
                    {menuItem.displayText}
                  </TopLevelClientAwareMenuLink>
                );
              }
              if (menuItem.openBehavior === "externalPage") {
                return (
                  <MenuLink
                    key={menuItem.to + idx.toString()}
                    onClick={() => onCommonMenuItemClick(menuItem)}
                    testId={`external-connection-menu-item-${idx}`}
                    to={menuItem.to}
                  >
                    <Shelf>
                      {menuItem.displayText}
                      <Icon name="link-out" />
                      <Shelf.Spacer />
                    </Shelf>
                  </MenuLink>
                );
              }
            })}
            <Shelf.Spacer />
          </DesktopMenuBarContainer>
        </Stack>
        <FullWidthDropdownMenu ref={claimsAndCoverageMenuRef} expanded={claimsAndCoverageMenuExpanded}>
          <StyledHeading>{translate("MENU_BAR.claimsAndCoverage")}</StyledHeading>
          <Stack>
            {claimsAndCoverageMenuItems.map((menuItem, idx) => {
              return (
                <div key={menuItem.to + idx.toString()} style={{ width: "fit-content" }}>
                  {menuItem.openBehavior === "externalPage" && (
                    <SubMenuLink
                      onClick={() => onCommonMenuItemClick(menuItem)}
                      testId={`claims-coverage-menu-item-${idx}`}
                      to={menuItem.to}
                    >
                      <Shelf>
                        {menuItem.displayText}
                        <Icon name="link-out" />
                        <Shelf.Spacer />
                      </Shelf>
                    </SubMenuLink>
                  )}
                  {!menuItem.openBehavior && (
                    <ClientAwareMenuLink
                      key={menuItem.to + idx.toString()}
                      testId={`claims-coverage-menu-item-${idx}`}
                      analyticsEvent={menuItem.analyticsEvent}
                      to={menuItem.to}
                    >
                      {menuItem.displayText}
                    </ClientAwareMenuLink>
                  )}
                </div>
              );
            })}
          </Stack>
        </FullWidthDropdownMenu>
        <FullWidthDropdownMenu ref={careSearchMenuRef} expanded={careSearchMenuExpanded}>
          <StyledHeading>{translate("MENU_BAR.careSearch")}</StyledHeading>
          <Stack>
            {careSearchMenuItems.map((menuItem, idx) => {
              return (
                <div key={menuItem.to + idx.toString()} style={{ width: "fit-content" }}>
                  {menuItem.openBehavior === "externalPage" && (
                    <SubMenuLink onClick={() => onCommonMenuItemClick(menuItem)} testId={`care-search-menu-item-${idx}`} to={menuItem.to}>
                      <Shelf>
                        {menuItem.displayText}
                        <Icon name="link-out" />
                        <Shelf.Spacer />
                      </Shelf>
                    </SubMenuLink>
                  )}
                  {!menuItem.openBehavior && (
                    <ClientAwareMenuLink
                      key={menuItem.to + idx.toString()}
                      testId={`care-search-menu-item-${idx}`}
                      analyticsEvent={menuItem.analyticsEvent}
                      to={menuItem.to}
                    >
                      {menuItem.displayText}
                    </ClientAwareMenuLink>
                  )}
                </div>
              );
            })}
          </Stack>
        </FullWidthDropdownMenu>
      </div>
    </Root>
  );
};
